import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Keyfigure.css";

const { Title } = Typography;

const AnimatedCounter = ({
  value = "0",
  duration = 2000,
  suffix = "",
  prefix = "",
  decimals = 2, // number of decimal places
}) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  // Convert Devanagari digits (०१२३४५६७८९) to English digits (0123456789)
  const toEnglishDigits = (str) =>
    str.replace(/[०-९]/g, (d) => "०१२३४५६७८९".indexOf(d));

  // normalize input once
  const normalized = toEnglishDigits(String(value)).replace(/,/g, "").replace("%", "").trim();
  const numericTarget = parseFloat(normalized) || 0;
  const targetIsInteger = Number.isInteger(numericTarget);

  useEffect(() => {
    if (!inView || !value) return;

    const frameRate = 16; // ~60fps
    const totalFrames = Math.max(1, Math.round(duration / frameRate));
    const increment = numericTarget / totalFrames;
    let current = 0;

    const counter = setInterval(() => {
      current += increment;
      if (current >= numericTarget) {
        clearInterval(counter);
        setCount(numericTarget);
      } else {
        setCount(current);
      }
    }, frameRate);

    return () => clearInterval(counter);
  }, [numericTarget, duration, inView, value]);

  // Detect if the original value had Devanagari numerals
  const isDevanagari = /[०-९]/.test(String(value));
  const devDigits = "०१२३४५६७८९";

  // Format the displayed value. If the target is an integer, show rounded integers during animation
  // Otherwise show decimals as requested by `decimals`.
  const formatValue = (num) => {
    if (targetIsInteger) {
      const intStr = Math.round(num).toLocaleString("en-IN");
      return isDevanagari ? intStr.replace(/\d/g, (d) => devDigits[parseInt(d)]) : intStr;
    }

    // decimal target - show fractional digits
    const formatted = Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    if (isDevanagari) return formatted.replace(/\d/g, (d) => devDigits[parseInt(d)]);
    return formatted;
  };

  const formattedValue = formatValue(count);
  const hasPercent = String(value).includes("%");

  // Trigger fade/slide-in animation on scroll
  useEffect(() => {
    if (inView) controls.start({ opacity: 1, y: 0 });
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={controls}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <Title level={3} className="card-value">
        {prefix}
        {formattedValue}
        {hasPercent ? "%" : suffix}
      </Title>
    </motion.div>
  );
};

export default AnimatedCounter;
